import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { formatTranslation } from 'intl';
import { IntlShape } from 'react-intl';
import { DataGridColumnsFactory } from 'components/common/dataGrid/columnsFactory';
import { CompanyOnView } from 'shared/domain/company/types/view';
import { FinancialData } from 'shared/domain/company/types/financialData';
import { ListCellWithModal } from 'components/common/dataGrid/columnsFactory/cellRenderers/listCellWithModal';
import { FiltersTranslationKeys } from 'intl/keys';

export type CompanyDataGridRow = CompanyOnView &
  FinancialData & {
    correspondenceAddress?: {
      streetAndNumber?: string;
      postalCode?: string;
      country?: string;
    };
    registeredOfficeAddress?: {
      streetAndNumber?: string;
      postalCode?: string;
      country?: string;
    };
  };

export class CompanyColumnsFactory extends DataGridColumnsFactory<CompanyDataGridRow> {
  constructor(
    intl: IntlShape,
    timezone: string,
    private organizationId: string
  ) {
    super(intl, timezone);
    this.organizationId = organizationId;
  }

  private readonly renderCompanySitesCell = (
    params: GridRenderCellParams<CompanyDataGridRow>
  ) => {
    // Use params.row.sites or params.value (from valueGetter) as fallback
    const sites = params.row.sites || params.value || [];
    // Ensure sites is an array of LabelledEntity with _id and label
    const validSites = Array.isArray(sites)
      ? sites.filter(
          (site): site is { _id: string; label: string } =>
            site && typeof site === 'object' && '_id' in site && 'label' in site
        )
      : [];
    return (
      <ListCellWithModal
        items={validSites}
        label={formatTranslation(this.intl, FiltersTranslationKeys.filterTypeSite)}
      />
    );
  };

  create(): GridColDef<CompanyDataGridRow>[] {
    return [
      this.orderNumber(),
      this.companyShortLabel(),
      this.companyLongLabel(),
      this.finalCostSettled(),
      this.finalCostOutstanding(),
      this.estimatedCost(),
      this.expectedFine(),
      this.imposedFine(),
      this.projectInvolvementType(),
      {
        ...this.companySites(),
        align: 'left',
        headerAlign: 'left',
        renderCell: this.renderCompanySitesCell,
      },
      this.taxNumber(),
      this.correspondenceAddressStreetAndNumber(),
      this.correspondenceAddressPostalCode(),
      this.correspondenceAddressCountry(),
      this.registeredAddressStreetAndNumber(),
      this.registeredAddressPostalCode(),
      this.registeredAddressCountry(),
      this.companyCreatedBy(),
      this.createdAt(this.intl, this.timezone),
      this.companyModifiedBy(),
      this.modifiedAt(this.intl, this.timezone),
    ];
  }
}

